<template>
  <v-card class="attachment">
    <v-progress-linear v-if="loading" :indeterminate="true" />
    <v-list v-if="this.contacts" two-line>
      <v-subheader>
        <v-flex>{{ $t('contacts.Header') }}</v-flex>
        <v-btn icon>
          <v-icon color="grey">search</v-icon>
        </v-btn>

        <!-- Add Contact Dialog -->
        <v-dialog v-model="dialogOpen" scrollable lazy>
          <template v-slot:activator="{ on }">
            <v-btn disabled icon>
              <v-icon color="grey" v-on="on">person_add</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="headline primary white--text">
              <v-icon class="mr-2" color="white">person_add</v-icon>
              <span>Add Contact</span>
            </v-card-title>

            <v-card-text>
              <v-layout>
                <v-flex xs2>
                  <v-autocomplete
                    v-model="model"
                    chips
                    clearable
                    :items="items"
                    :loading="isLoading"
                    :search-input.sync="search"
                    allow-overflow
                    dense
                    hide-no-data
                    item-text="username"
                    item-value="id"
                    label="Search Contacts"
                    placeholder
                    prepend-icon="search"
                    return-object
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        :key="`selection-${data.id}`"
                        class="v-chip--select-multi"
                        @click.stop="data.parent.selectedIndex = data.index"
                        @input="data.parent.selectItem(data.item)"
                      >
                        <v-avatar class="accent white--text">
                          <img :src="data.item.picture" />
                        </v-avatar>
                        {{ data.item.username }}
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <v-chip :key="data.id" class="v-chip--select-multi">
                        <v-avatar class="accent white--text">
                          <img :src="data.item.picture" />
                        </v-avatar>
                        {{ data.item.username }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-btn
                  depressed
                  color="success"
                  @click="addContact"
                  :loading="loadingAddContact"
                  >Connect</v-btn
                >
              </v-layout>
              <v-layout>
                <v-flex xs2>
                  <v-form ref="form" @submit.prevent>
                    <v-text-field
                      v-model="inviteEmail"
                      prepend-icon="mail_outline"
                      label="Invite Collegue"
                      hint="Collegue not on Xerpa?"
                      persistent-hint
                      class="mb-2"
                      validate-on-blur
                      :rules="[rules.email]"
                    />
                  </v-form>
                </v-flex>
                <v-btn @click="inviteUser" depressed color="primary"
                  >Invite</v-btn
                >
              </v-layout>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="warning" flat @click="dialogOpen = false"
                >Close</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- End Add Contact Dialog -->
      </v-subheader>
      <template v-for="(contact, index) in contacts">
        <v-divider inset v-if="index !== 0" :key="`d-${contact.id}`" />
        <v-list-tile
          :key="`lt-${contact.id}`"
          avatar
          @click="
            $router.push({ name: 'Contact', params: { contactId: contact.id } })
          "
        >
          <v-list-tile-avatar>
            <img :src="contact.picture" />
          </v-list-tile-avatar>

          <v-list-tile-content>
            <v-list-tile-title>{{ contact.username }}</v-list-tile-title>
            <v-list-tile-sub-title>
              <span class="text--primary">{{
                `${(contact.roles || []).join(', ')}`
              }}</span>
              <span class="text--primary" v-if="contact.specialty">{{
                ` | ${contact.specialty}`
              }}</span>
              <span v-if="contact.hospital">{{
                ` — ${getHospital(contact.hospital)}`
              }}</span>
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
      </template>
    </v-list>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import firestore from '@/firebase'
import { Hospital } from '../../models/Content'
import axios from 'axios'
export default {
  data() {
    return {
      loading: true,
      loadingAddContact: false,
      invitationLoading: false,
      inviteEmail: '',
      descriptionLimit: 60,
      entries: [],
      isLoading: false,
      model: null,
      search: null,
      users: null,
      dialogOpen: false,
      rules: {
        email: value => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        }
      }
    }
  },
  watch: {
    // eslint-disable-next-line no-unused-vars
    search(val) {
      // Items have already been loaded
      if (this.items.length > 0) return

      // Items have already been requested
      if (this.isLoading) return

      this.isLoading = true
      // TODO: handle error
      this.getUsers().finally(() => (this.isLoading = false))
    }
  },
  computed: {
    ...mapGetters(['contacts', 'user']),
    fields() {
      if (!this.model) return []

      return Object.keys(this.model).map(key => {
        return {
          key,
          value: this.model[key] || 'n/a'
        }
      })
    },
    items() {
      return (this.users || []).map(contact => {
        return {
          username: contact.username || contact.email,
          id: contact.id,
          picture: contact.picture,
          license: contact.license
        }
      })
    }
  },
  created() {
    this.getContacts()
  },
  methods: {
    ...mapActions([
      'sendContactRequest',
      'showSuccessSnackbar',
      'showErrorSnackbar'
    ]),
    getContacts() {
      return this.$store
        .dispatch('getContacts', this.user)
        .then((this.loading = false))
        .catch(err => {
          this.$store.dispatch(
            'showErrorSnackbar',
            err.message || 'Something went wrong'
          )
        })
    },
    addContact() {
      const userId = this.model.id
      if ((this.user.contacts || []).find(c => c.id === userId))
        return this.showErrorSnackbar('User already in contact list')
      if (userId) {
        this.loadingAddContact = true
        this.$store
          .dispatch('addContact', userId)
          .then(() => {
            return this.showSuccessSnackbar('Connection request sent!')
          })
          .then(() => {
            // TODO: find out why this does not update state
            return this.getContacts()
          })
          .catch(err => {
            this.showErrorSnackbar(err.message || 'Something went wrong')
          })
          .finally(() => {
            this.loadingAddContact = false
          })
      }
    },
    getUsers() {
      return axios
        .get(`/users`, {
          headers: {
            Authorization: `Bearer ${this.$store.getters.accessToken}`
          }
        })
        .then(response => {
          const users = response.data.filter(u => u.id !== this.user.id)

          this.users = users
        })
        .catch(err => {
          this.showErrorSnackbar(err.message || 'Something went wrong')
        })
    },
    getHospital(hospitalId) {
      return Hospital.query().whereId(hospitalId).first()
      // return cms.getHospitals().find(h => h.value == hospitalId).text
    },
    inviteUser() {
      if (this.$refs.form.validate()) {
        this.invitationLoading = true
        // TODO: fix urls
        // const url = `http://localhost:5000/xerpa-40c1c/us-central1/api/v1/auth0/users`;
        const url =
          'https://us-central1-xerpa-40c1c.cloudfunctions.net/webapi/v1/auth0/users'
        // const url = `${window.location.origin}/api/v1/auth0/users`;
        // const url = `${window.location.origin}/api/v1/users`;
        const body = {
          email: this.inviteEmail,
          from: this.user.id
        }
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
          .then(res => {
            if (res.ok) {
              return res.json()
            } else {
              throw Error('Could not invite user')
            }
          })
          .then(() => {
            this.showSuccessSnackbar('User invited')
          })
          .catch(err => {
            this.showErrorSnackbar(err.message || 'Something went wrong')
          })
          .finally(() => {
            this.invitationLoading = false
          })
      }
    }
  }
}
</script>
